import React, { useState, useEffect } from "react";
import useIsMobileDevice from "../../../shared/hooks/useIsMobileDevice";
import useDeviceType from "../../../shared/hooks/useDeviceType";
import { useMobileNavigation } from "../../../contexts/MobileNavigationContext";
import "./styles/appdownloadmodal.css";

interface AppDownloadModalProps {
  onClose?: () => void;
  onDownload?: () => void;
  onContinueWeb?: () => void;
}

const AppDownloadModal: React.FC<AppDownloadModalProps> = ({
  onClose,
  onDownload,
  onContinueWeb,
}) => {
  const [visible, setVisible] = useState(true);
  const isMobileDevice = useIsMobileDevice();
  const deviceType = useDeviceType();
  const { hideNavigation, showNavigation } = useMobileNavigation();

  // Hide navigation when modal opens
  useEffect(() => {
    if (visible && isMobileDevice) {
      hideNavigation();
    }
    
    // Show navigation when modal closes
    return () => {
      showNavigation();
    };
  }, [visible, isMobileDevice, hideNavigation, showNavigation]);

  // Don't render modal at all if not on a mobile device
  if (!isMobileDevice) return null;

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  const handleDownload = () => {
    // App store URLs - replace with your actual BuildZap app URLs
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.buildzap&hl=en_IN"; // Replace with actual Android package name
    const appStoreUrl = "https://apps.apple.com/in/app/buildzap-materials-in-minutes/id6760388766"; // Replace with actual App Store ID

    if (deviceType === 'android') {
      // Redirect to Play Store for Android devices
      window.open(playStoreUrl, '_blank');
    } else if (deviceType === 'ios') {
      // Redirect to App Store for iOS devices
      window.open(appStoreUrl, '_blank');
    } else {
      // Fallback - open Play Store as default
      window.open(playStoreUrl, '_blank');
    }

    // Close modal after redirect
    setVisible(false);
    onDownload?.();
  };

  const handleContinueWeb = () => {
    setVisible(false);
    onContinueWeb?.();
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay">

      <button className="close-button-modal" onClick={handleClose} aria-label="Close">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      {/* Mobile screen wrapper - modal only shows inside mobile viewport */}
      <div className="mobile-screen-wrapper">
        <div className="modal-sheet">

          {/* Cross / Close button - outside main card, at top right */}


          {/* Main card - background image area */}
          <div className="main-card-modal">

            {/* Background image section */}
            <div className="main-card-bg">
              <img
                src='https://ik.imagekit.io/raghav11/mobileimage.png'
                alt="App preview"
                onError={(e) => {
                  // Fallback: hide broken img and show gradient via CSS
                  const target = e.currentTarget as HTMLImageElement;
                  target.style.display = "none";
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.background =
                      "linear-gradient(135deg, #ffe066 0%, #ffd700 40%, #ffb800 70%, #e6a800 100%)";
                    // Add decorative phone shapes as fallback visual
                    parent.innerHTML = `
                      <div style="display:flex;align-items:flex-end;justify-content:center;gap:10px;padding:20px;height:100%;box-sizing:border-box;">
                        <div style="width:68px;height:130px;background:white;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.15);opacity:0.85;"></div>
                        <div style="width:78px;height:155px;background:white;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.2);"></div>
                        <div style="width:65px;height:120px;background:white;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.15);opacity:0.85;"></div>
                      </div>
                    `;
                  }
                }}
              />
            </div>

            {/* Inner white card */}
            <div className="inner-card">

              {/* Square icon - centered, half on bg / half on inner card */}
              <div className="icon-wrapper">
                <div className="icon-box">
                  <img
                    src="/images/icon.png"
                    alt="BuildZap icon"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        parent.style.background = "#ffd700";
                        parent.style.fontSize = "28px";
                        parent.innerHTML = "⚡";
                      }
                    }}
                  />
                </div>
              </div>

              {/* Text content */}
              <div className="modal-text-content">
                <p className="get-the-app-text">Get BuildZap app for</p>
                <h2 className="better-experience-text">Better Experience</h2>
                <p className="live-tracking-text">Live Tracking • Exclusive Offers</p>
              </div>

              {/* Download button */}
              <button className="download-btn" onClick={handleDownload}>
                Download the app now
              </button>

              {/* Continue on web button */}
              <button className="continue-web-btn" onClick={handleContinueWeb}>
                Continue on web
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AppDownloadModal;